import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'

import {
  getPayoutRequisiteOptions,
  rebuildPayoutSnapshot,
  replacePayoutRequisite,
} from '../../core/_requests'
import {
  MatchingPayoutRequisite,
  PayoutRequisiteOption,
  REQUISITE_KIND_LABELS,
  ReplacePayoutRequisiteBody,
} from '../../core/_models'

interface Props {
  show: boolean
  payoutToken: string
  /** Каналы выплаты из строки списка. Пустой массив — менять нечего, кнопка не должна открывать окно. */
  requisites: MatchingPayoutRequisite[]
  onClose: () => void
  onReplaced: (channel: MatchingPayoutRequisite) => void
}

/** Как называется поле идентификатора у разного вида реквизита. */
const IDENTIFIER_LABELS: Record<string, string> = {
  bank_card: 'Номер карты',
  phone_number: 'Телефон',
  bank_account: 'Номер счёта',
  crypto_wallet: 'Адрес кошелька',
  redirect_url: 'Ссылка',
}

/**
 * Смена реквизита активной matching-выплаты (CPAYMENT-1733).
 *
 * Два запроса подряд: платёжка меняет канал выплаты, затем мэтчинг пересобирает замороженный
 * конфиг. Второй идемпотентен, поэтому его сбой — не потеря: реквизит уже заменён и новые депозиты
 * получают именно его, окно просто предлагает повторить пересборку.
 *
 * Платёжный метод не выбирается: бэкенд берёт его у заменяемого канала. Иначе новый реквизит увёл
 * бы выплату на другой метод и выбил её из кандидатов половины депозитов.
 */
export const ReplacePayoutRequisiteModal: React.FC<Props> = ({
  show,
  payoutToken,
  requisites,
  onClose,
  onReplaced,
}) => {
  const [channelToken, setChannelToken] = useState<string | null>(null)
  const [options, setOptions] = useState<PayoutRequisiteOption[]>([])
  const [selectedToken, setSelectedToken] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [identifier, setIdentifier] = useState('')
  const [cardHolder, setCardHolder] = useState('')
  const [acquiringBank, setAcquiringBank] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [replacedChannel, setReplacedChannel] = useState<MatchingPayoutRequisite | null>(null)
  const [optionsState, setOptionsState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle')

  // Выбор канала нужен только составной выплате; при одном канале он предопределён.
  const needsChannelChoice = requisites.length > 1

  const channel = useMemo(
    () => requisites.find((r) => r.token === channelToken) ?? null,
    [requisites, channelToken]
  )

  const kind = channel?.kind ?? 'bank_card'
  const identifierLabel = IDENTIFIER_LABELS[kind] ?? 'Реквизит'
  const bankRequired = kind === 'phone_number'

  const reset = useCallback(() => {
    setChannelToken(requisites.length === 1 ? requisites[0].token : null)
    setOptions([])
    setSelectedToken(null)
    setAdding(false)
    setIdentifier('')
    setCardHolder('')
    setAcquiringBank('')
    setFieldError(null)
    setGeneralError(null)
    setReplacedChannel(null)
    setOptionsState('idle')
  }, [requisites])

  useEffect(() => {
    if (show) {
      reset()
    }
  }, [show, reset])

  useEffect(() => {
    if (!show || !channelToken) {
      return
    }

    // Гонка при быстрой смене канала: ответ по прошлому каналу не должен перетереть текущий список.
    let current = true
    setOptionsState('loading')

    getPayoutRequisiteOptions(payoutToken, channelToken)
      .then((res) => {
        if (!current) return
        if (res.data?.success) {
          setOptions(res.data.data ?? [])
          setOptionsState('ready')
          return
        }
        setOptions([])
        setOptionsState('failed')
      })
      .catch(() => {
        if (!current) return
        // Не выдаём сбой загрузки за «у клиента нет реквизитов» — иначе оператор заведёт дубль.
        setOptions([])
        setOptionsState('failed')
      })

    return () => {
      current = false
    }
  }, [show, payoutToken, channelToken])

  const buildBody = (): ReplacePayoutRequisiteBody | null => {
    if (!channelToken) {
      return null
    }

    if (!adding) {
      if (!selectedToken) {
        setFieldError('Выберите реквизит')
        return null
      }
      return { channelToken, requisiteToken: selectedToken }
    }

    if (!identifier.trim()) {
      setFieldError(`Укажите ${identifierLabel.toLowerCase()}`)
      return null
    }
    if (bankRequired && !acquiringBank.trim()) {
      setFieldError('Укажите банк')
      return null
    }

    return {
      channelToken,
      identifier: identifier.trim(),
      ...(cardHolder.trim() ? { cardHolder: cardHolder.trim() } : {}),
      ...(acquiringBank.trim() ? { acquiringBank: acquiringBank.trim() } : {}),
    }
  }

  const runRebuild = async (channelAfter: MatchingPayoutRequisite): Promise<void> => {
    try {
      await rebuildPayoutSnapshot(payoutToken)
      onReplaced(channelAfter)
      onClose()
    } catch {
      // Реквизит заменён; не пересобран только замороженный конфиг выплаты.
      setReplacedChannel(channelAfter)
      setGeneralError(
        'Реквизит заменён, но конфигурация выплаты в мэтчинге не пересобрана. Повторите — повторный вызов безопасен.'
      )
    }
  }

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()

    // Реквизит уже заменён, ждём только повтора пересборки. Форма остаётся в DOM, и Enter в поле
    // отправил бы замену повторно — уже несуществующим каналом.
    if (replacedChannel) {
      return
    }

    setFieldError(null)
    setGeneralError(null)

    const body = buildBody()
    if (!body) {
      return
    }

    setSubmitting(true)
    try {
      const response = await replacePayoutRequisite(payoutToken, body)
      const payload = response.data
      if (!payload || payload.success !== true || !payload.data) {
        setGeneralError(errorText(payload && payload.success === false ? payload : undefined))
        return
      }
      await runRebuild(payload.data)
    } catch (e: unknown) {
      setGeneralError(errorText((e as ApiErrorShape)?.response?.data))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRetryRebuild = async (): Promise<void> => {
    if (!replacedChannel) {
      return
    }
    setSubmitting(true)
    setGeneralError(null)
    try {
      await runRebuild(replacedChannel)
    } finally {
      setSubmitting(false)
    }
  }

  // Закрытие после частичного сбоя: реквизит в БД уже новый, поэтому строку надо перечитать, иначе
  // оператор увидит старый номер и решит, что замена не прошла.
  const handleClose = (): void => {
    if (replacedChannel) {
      onReplaced(replacedChannel)
      return
    }
    onClose()
  }

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Изменить реквизит выплаты</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {needsChannelChoice && !channelToken && (
            <div className='mb-3'>
              <div className='form-label'>Какой реквизит заменить</div>
              {requisites.map((requisite) => (
                <button
                  key={requisite.token}
                  type='button'
                  className='btn btn-light w-100 text-start mb-2'
                  onClick={() => setChannelToken(requisite.token)}
                >
                  <span className='text-muted fs-7 d-block'>
                    {requisite.kind ? REQUISITE_KIND_LABELS[requisite.kind] : 'Реквизит'}
                  </span>
                  <span className='fw-bold'>{requisite.identifier}</span>
                </button>
              ))}
            </div>
          )}

          {channelToken && (
            <>
              <div className='mb-2 text-muted small d-flex align-items-center gap-2'>
                <span>
                  Заменяем: <span className='fw-bold'>{channel?.identifier}</span>
                </span>
                {needsChannelChoice && !replacedChannel && (
                  <button
                    type='button'
                    className='btn btn-link btn-sm p-0'
                    onClick={() => {
                      setChannelToken(null)
                      setSelectedToken(null)
                      setAdding(false)
                      setFieldError(null)
                    }}
                  >
                    выбрать другой
                  </button>
                )}
              </div>

              {!adding && (
                <div className='mb-3'>
                  <div className='form-label'>Выберите реквизит</div>
                  {optionsState === 'loading' && (
                    <div className='text-muted small mb-2'>Загружаем реквизиты клиента…</div>
                  )}
                  {optionsState === 'failed' && (
                    <div className='text-danger small mb-2'>
                      Не удалось загрузить реквизиты клиента. Можно добавить новый.
                    </div>
                  )}
                  {optionsState === 'ready' && options.length === 0 && (
                    <div className='text-muted small mb-2'>
                      У клиента нет других реквизитов этого типа — добавьте новый.
                    </div>
                  )}
                  {options.map((option) => (
                    <label key={option.token} className='d-block mb-2'>
                      <input
                        type='radio'
                        className='form-check-input me-2'
                        name='requisite-option'
                        value={option.token}
                        checked={selectedToken === option.token}
                        onChange={() => setSelectedToken(option.token)}
                        aria-label={option.identifier}
                      />
                      <span className='fw-bold'>{option.identifier}</span>
                      {option.bank && <span className='text-muted small ms-2'>{option.bank}</span>}
                    </label>
                  ))}
                  <button type='button' className='btn btn-light btn-sm' onClick={() => setAdding(true)}>
                    Добавить
                  </button>
                </div>
              )}

              {adding && (
                <div className='mb-3'>
                  <div className='mb-2'>
                    <label className='form-label required' htmlFor='new-requisite-identifier'>
                      {identifierLabel}
                    </label>
                    <input
                      id='new-requisite-identifier'
                      className='form-control'
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                    />
                  </div>
                  <div className='mb-2'>
                    <label
                      className={bankRequired ? 'form-label required' : 'form-label'}
                      htmlFor='new-requisite-bank'
                    >
                      Банк
                    </label>
                    <input
                      id='new-requisite-bank'
                      className='form-control'
                      value={acquiringBank}
                      onChange={(e) => setAcquiringBank(e.target.value)}
                    />
                  </div>
                  <div className='mb-2'>
                    <label className='form-label' htmlFor='new-requisite-cardholder'>
                      Держатель
                    </label>
                    <input
                      id='new-requisite-cardholder'
                      className='form-control'
                      value={cardHolder}
                      onChange={(e) => setCardHolder(e.target.value)}
                    />
                  </div>
                  <button type='button' className='btn btn-light btn-sm' onClick={() => setAdding(false)}>
                    Выбрать существующий
                  </button>
                </div>
              )}
            </>
          )}

          {fieldError && <div className='text-danger small'>{fieldError}</div>}
          {generalError && <div className='alert alert-danger py-2 mt-2'>{generalError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={handleClose} disabled={submitting}>
            Отмена
          </button>
          {replacedChannel ? (
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleRetryRebuild}
              disabled={submitting}
            >
              Повторить
            </button>
          ) : (
            <button type='submit' className='btn btn-primary' disabled={submitting || !channelToken}>
              {submitting ? 'Меняем…' : 'Заменить'}
            </button>
          )}
        </Modal.Footer>
      </form>
    </Modal>
  )
}

interface ApiErrorShape {
  response?: {
    data?: { errors?: Record<string, string | undefined>; errorCode?: string; message?: string }
  }
}

const ERROR_TEXTS: Record<string, string> = {
  PAYOUT_REQUISITE_NOT_EDITABLE: 'Выплата уже не активна в мэтчинге — реквизит менять нельзя',
  PAYOUT_REQUISITE_CHANNEL_UNKNOWN: 'Этот реквизит не принадлежит выплате',
  PAYOUT_REQUISITE_METHOD_MISMATCH: 'Новый реквизит должен быть того же платёжного метода',
}

function errorText(payload?: {
  errors?: Record<string, string | undefined>
  errorCode?: string
  message?: string
}): string {
  const byCode = payload?.errorCode ? ERROR_TEXTS[payload.errorCode] : undefined
  if (byCode) {
    return byCode
  }
  const errors = payload?.errors ?? {}
  const first = Object.values(errors)[0]

  return first ?? payload?.message ?? 'Не удалось заменить реквизит'
}
